import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { CoverImage } from '@/components/CoverImage';
import { useReadersAlsoLiked } from '@/hooks/useReadersAlsoLiked';
import { Users } from 'lucide-react';

interface ReadersAlsoLikedProps {
  bookId: string;
}

export function ReadersAlsoLiked({ bookId }: ReadersAlsoLikedProps) {
  const { data: books, isLoading } = useReadersAlsoLiked(bookId);

  // Don't show section if loading or no books found
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Readers Also Liked
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-24 space-y-2">
                <Skeleton className="aspect-[2/3] w-full rounded-md" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Hide section if no results
  if (!books || books.length < 2) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Readers Also Liked
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full">
          <div className="flex gap-4 pb-4">
            {books.map((book) => (
              <Link
                key={book.id}
                to={`/book/${book.id}`}
                className="flex-shrink-0 w-24 group"
              >
                <CoverImage
                  bookId={book.id}
                  isbn13={book.isbn13 || undefined}
                  isbn10={book.isbn10 || undefined}
                  olid={book.open_library_id || undefined}
                  title={book.title}
                  author={book.authors?.[0]}
                  coverUrl={book.cover_url || undefined}
                  coverCacheUrl={book.cover_cache_url || undefined}
                  size="sm"
                  className="rounded-md transition-transform group-hover:scale-105"
                />
                <p className="mt-2 text-xs font-medium line-clamp-2 group-hover:text-primary transition-colors">
                  {book.title}
                </p>
                {book.authors?.[0] && (
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {book.authors[0]}
                  </p>
                )}
              </Link>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
